import React, { useCallback, ChangeEvent, forwardRef } from 'react';
import { TextFieldProps } from './types';
import { generateId } from '../../utils/generateId';

import { Input, Textarea } from './styled';

const NUMBER_REGEX = /^-?[0-9]*\.?[0-9]+$/;

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      id,
      className,
      disabled = false,
      hasError,
      multiline = false,
      spellCheck = false,
      name,
      onChange,
      placeholder,
      size = 'base',
      type = 'text',
      value,
      ...otherProps
    }: TextFieldProps,
    ref,
  ) => {
    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (!onChange) return;
        const newValue = event.currentTarget.value;

        if (newValue === '') {
          onChange(newValue);
          return;
        }

        if (type !== 'number') {
          onChange(newValue);
          return;
        }

        const reg = new RegExp(NUMBER_REGEX);
        if (!reg.test(newValue)) {
          return;
        }

        onChange(newValue);
      },
      [onChange, type],
    );

    const componentId = id ?? generateId('textfield');

    if (type === 'text' && multiline) {
      return (
        <Textarea
          className={className}
          disabled={disabled}
          hasError={hasError}
          id={componentId}
          name={name}
          onChange={handleChange}
          placeholder={placeholder}
          value={value}
          ref={ref}
          spellCheck={spellCheck}
          {...otherProps}
        />
      );
    }

    return (
      <Input
        className={className}
        disabled={disabled}
        hasError={hasError}
        id={componentId}
        compact={size === 'sm'}
        name={name}
        aria-label={name}
        onChange={handleChange}
        placeholder={placeholder}
        type={type}
        value={value}
        ref={ref}
        spellCheck={spellCheck}
        {...otherProps}
      />
    );
  },
);
